import React from 'react';
import classNames from 'classnames';

import { Icon } from 'js/design_system/Icon';
import { BaseTextButtonProps } from '../TextButton';
import { trackClick } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/text_button.scss';

type TextButtonLinkProps = {
  external?: boolean;
} & BaseTextButtonProps &
  React.ComponentPropsWithoutRef<'a'>;

/**
 * A textual navigation link, for use alone or in a ButtonGroup. Supports all of the native
 * attributes of an anchor element (<a>) - you should at least specify `href`. Also supports
 * these custom attributes:
 *
 * Props:
 * - `external`: Whether the link should act as an external link (default: false)
 * - `size`: The size of the button - one of 'sm' or 'lg' (default: 'lg')
 * - `inline`: Button without a set height or padding, to look and act like inline text (default: false)
 * - `inverse`: Button to be used on darker backgrounds (default: false)
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as
 *     the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render
 *     as the button's icon (right of the text)
 * - `trackClick`: Options for tracking clicks on this button link
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
 *
 * <TextButtonLink href="/about" external>
 *   Click me!
 * </TextButtonLink>
 * ```
 *
 */
export const TextButtonLink = React.forwardRef<
  HTMLAnchorElement,
  TextButtonLinkProps
>(
  (
    {
      external,
      size = 'lg',
      inline = false,
      inverse = false,
      icon,
      iconRight,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      rel: relProp,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds2-5-text-link', className, {
      'ds2-5-text-link--small': size === 'sm',
      'ds2-5-text-link--inline': inline,
      'ds2-5-text-link--inverse': inverse,
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? <Icon name={icon} size={18} /> : icon;
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={20} />
        ) : (
          iconRight
        );
    } else if (external) {
      iconRightView = <Icon name="arrow_outward" size={20} />;
    }

    const rel = [relProp, external ? 'noreferrer' : ''].filter(Boolean);

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      // This IS the design system implementation:
      // eslint-disable-next-line no-restricted-syntax
      <a
        className={classes}
        target={external ? '_blank' : undefined}
        {...restProps}
        rel={rel.length > 0 ? rel.join(' ') : undefined}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children && (
          <span className="ds2-5-text-link__content">{children}</span>
        )}
        {iconRightView ? iconRightView : null}
      </a>
    );
  },
);
